import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { usePageState } from "../context/AppStateContext";
import { useAuth } from "../context/AuthContext";
import { applyDisplayPrice } from "../utils/displayPrice";
import { normalizeRole } from "../utils/rolePermissions";
import {
  normalizeConditionScore,
  isConditionAcceptable,
} from "../utils/conditionScore";
import "./SorterPage.css";
import AutoFillLoader from "../components/Common/Loaders/AutoFillLoader";

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────
const API_BASE =
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api";
const ERP_UPLOAD_URL = "https://erp.thriftops.com/Upload/appImages.php";
const DEMO_COMPANY_ID = "13";
const DEMO_LIMIT = 100;

const AUTO_FILL_MESSAGES = [
  "Analyzing image...",
  "Fetching product details...",
  "Checking authentication...",
  "Assessing condition...",
  "Generating description...",
];

// ─────────────────────────────────────────────
// IMAGE UPLOAD STATUS
// Each image goes through these states independently
// ─────────────────────────────────────────────
const UPLOAD_STATUS = {
  PENDING: "pending", // just selected, upload not started
  UPLOADING: "uploading", // upload in progress
  SUCCESS: "success", // uploaded, URL available
  FAILED: "failed", // upload failed — corrupt or network error
};

// ─────────────────────────────────────────────
// PAGE STATE MACHINE
// ─────────────────────────────────────────────
const PAGE_STATE = {
  IDLE: "idle",
  LOADING: "loading",
  PROCESSING: "processing",
  SAVING: "saving",
  ERROR: "error",
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const isOwnerOrAdmin = (user) => {
  const role = normalizeRole(user?.role);
  return role === "owner" || role === "admin";
};

const getThriftopsSuggestion = (authScore, conditionScore) => {
  const auth = parseFloat(authScore) || 0;
  const cond = normalizeConditionScore(conditionScore);
  if (auth >= 80 && cond >= 8) return "Sell Online";
  return "Wholesale";
};

const mapToResultRow = (
  item,
  displayPricePercentage,
  user,
  previewImage = null,
) => ({
  image: previewImage || (item.images?.length > 0 ? item.images[0] : null),
  description: item.title,
  price: isOwnerOrAdmin(user)
    ? item.price_recommended
    : applyDisplayPrice(item.price_recommended, displayPricePercentage),
  unit: item.currency || " ",
  authScore: item.authenticity_score,
  conditionScore: normalizeConditionScore(item.condition_score),
  suggestion: getThriftopsSuggestion(
    item.authenticity_score,
    item.condition_score,
  ),
  authReasons: item.authenticity_summary
    ? [{ status: "pass", text: item.authenticity_summary }]
    : [],
  conditionReasons: item.condition_summary
    ? [{ status: "pass", text: item.condition_summary }]
    : [],
  raw: item,
});

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────
const SorterPage = () => {
  // ── State ────────────────────────────────────────────────
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  // Each image: { id, file, preview, uploadStatus, uploadedUrl, error }
  const [images, setImages] = useState([]);
  const [results, setResults] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [autoFillMsgIndex, setAutoFillMsgIndex] = useState(0);
  const [processingError, setProcessingError] = useState(null);

  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const replacingIdRef = useRef(null); // which image is being replaced
  const resultsRef = useRef(null);

  const { displayPricePercentage, minimumAcceptableCondition, user } =
    useAuth();
  const { setState: setAddProductState } = usePageState("addProduct");
  const navigate = useNavigate();

  const company_id = localStorage.getItem("company_id");
  const user_id = JSON.parse(localStorage.getItem("user") || "{}")?.id;

  const isBusy =
    pageState === PAGE_STATE.PROCESSING || pageState === PAGE_STATE.SAVING;

  // ── Derived: is Process button active? ───────────────────
  // Active only when: at least 1 image, none uploading, none failed
  const hasImages = images.length > 0;
  const anyUploading = images.some(
    (img) => img.uploadStatus === UPLOAD_STATUS.UPLOADING,
  );
  const anyFailed = images.some(
    (img) => img.uploadStatus === UPLOAD_STATUS.FAILED,
  );
  const anyPending = images.some(
    (img) => img.uploadStatus === UPLOAD_STATUS.PENDING,
  );
  const canProcess =
    hasImages && !anyUploading && !anyFailed && !anyPending && !isBusy;

  // ── Auto-rotate loading messages ─────────────────────────
  useEffect(() => {
    if (pageState !== PAGE_STATE.PROCESSING) return;
    const interval = setInterval(() => {
      setAutoFillMsgIndex((prev) => (prev + 1) % AUTO_FILL_MESSAGES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [pageState]);

  // ── Fetch saved results on mount ─────────────────────────
  useEffect(() => {
    const fetchResults = async () => {
      setPageState(PAGE_STATE.LOADING);
      try {
        const res = await axios.get(`${API_BASE}/smartscan/list-all`, {
          params: { company_id, user_id },
        });
        if (res.data.success) {
          setResults(
            res.data.data.map((item) =>
              mapToResultRow(item, displayPricePercentage, user),
            ),
          );
        }
      } catch (err) {
        console.error("[SorterPage] Failed to load saved results:", err);
      } finally {
        setPageState(PAGE_STATE.IDLE);
      }
    };
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────
  // Upload a single image to ERP immediately after selection.
  // Updates that image's status in state as it progresses.
  // ─────────────────────────────────────────────
  const uploadImageToERP = useCallback(async (id, file, preview) => {
    // Mark as uploading
    setImages((prev) =>
      prev.map((img) =>
        img.id === id ? { ...img, uploadStatus: UPLOAD_STATUS.UPLOADING } : img,
      ),
    );

    try {
      const blob = await fetch(preview).then((r) => r.blob());
      const form = new FormData();
      form.append("images[]", blob, file?.name || "image.jpg");

      const res = await axios.post(ERP_UPLOAD_URL, form, { timeout: 20000 });

      const url =
        res.data?.[0] || res.data?.images?.[0] || res.data?.data?.[0] || null;

      if (url) {
        // Success
        setImages((prev) =>
          prev.map((img) =>
            img.id === id
              ? {
                  ...img,
                  uploadStatus: UPLOAD_STATUS.SUCCESS,
                  uploadedUrl: url,
                }
              : img,
          ),
        );
      } else {
        // Got a response but no URL — treat as failed
        throw new Error("No URL in response");
      }
    } catch (err) {
      console.error(
        `[SorterPage] ERP upload failed for ${file?.name}:`,
        err.message,
      );
      setImages((prev) =>
        prev.map((img) =>
          img.id === id
            ? {
                ...img,
                uploadStatus: UPLOAD_STATUS.FAILED,
                error: "Corrupt or invalid image. Please remove or replace it.",
              }
            : img,
        ),
      );
    }
  }, []);

  // ─────────────────────────────────────────────
  // Add new image(s) — read preview, add to state, then upload immediately
  // ─────────────────────────────────────────────
  const addImages = useCallback(
    (files) => {
      const imageFiles = files.filter((f) => f.type.startsWith("image/"));

      imageFiles.forEach((file) => {
        const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const reader = new FileReader();

        reader.onloadend = () => {
          const preview = reader.result;

          // Add to state as PENDING first
          setImages((prev) => [
            ...prev,
            {
              id,
              file,
              preview,
              uploadStatus: UPLOAD_STATUS.PENDING,
              uploadedUrl: null,
              error: null,
            },
          ]);

          // Immediately start uploading
          uploadImageToERP(id, file, preview);
        };

        reader.readAsDataURL(file);
      });
    },
    [uploadImageToERP],
  );

  // ─────────────────────────────────────────────
  // Replace a failed image
  // ─────────────────────────────────────────────
  const handleReplaceClick = (id) => {
    replacingIdRef.current = id;
    replaceInputRef.current?.click();
  };

  const handleReplaceFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !replacingIdRef.current) return;

    const id = replacingIdRef.current;
    replacingIdRef.current = null;
    e.target.value = "";

    const reader = new FileReader();
    reader.onloadend = () => {
      const preview = reader.result;

      // Swap the image in place, reset to PENDING
      setImages((prev) =>
        prev.map((img) =>
          img.id === id
            ? {
                ...img,
                file,
                preview,
                uploadStatus: UPLOAD_STATUS.PENDING,
                uploadedUrl: null,
                error: null,
              }
            : img,
        ),
      );

      // Upload the new file
      uploadImageToERP(id, file, preview);
    };
    reader.readAsDataURL(file);
  };

  // ── Other image handlers ──────────────────────────────────
  const handleUploadAreaClick = (e) => {
    if (isBusy) return;
    if (e.target.closest(".remove-btn, .replace-btn, .preview-actions")) return;
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) addImages(files);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addImages(Array.from(e.dataTransfer.files));
  };

  const handleRemoveImage = (id) =>
    setImages((prev) => prev.filter((img) => img.id !== id));

  const handleClearAll = () => setImages([]);

  // ── Usage limit check ─────────────────────────────────────
  const checkUsageLimit = async () => {
    if (company_id !== DEMO_COMPANY_ID) return true;
    try {
      const res = await axios.get(`${API_BASE}/smartscan/counts`, {
        params: { company_id },
      });
      if (!res.data?.success) return true;
      const { smartscan = 0, products = 0 } = res.data.data;
      if (Number(smartscan) >= DEMO_LIMIT || Number(products) >= DEMO_LIMIT) {
        alert(
          "Your SmartScan demo limit has been reached (100 items). " +
            "Please contact ThriftOps support to upgrade your plan.",
        );
        return false;
      }
      return true;
    } catch (err) {
      console.error("[SorterPage] Usage limit check failed:", err);
      return true;
    }
  };

  // ─────────────────────────────────────────────
  // MAIN FLOW:
  // Images are already uploaded to ERP at this point.
  // Step 1 — Send original files to AI (multipart)
  // Step 2 — Save AI result + ERP URLs to DB
  // Step 3 — Update UI
  // ─────────────────────────────────────────────
  const handleProcessImages = async () => {
    if (!canProcess) return;

    const canProceed = await checkUsageLimit();
    if (!canProceed) return;

    // Snapshot successful images only
    const successImages = images.filter(
      (img) => img.uploadStatus === UPLOAD_STATUS.SUCCESS,
    );
    const uploadedUrls = successImages.map((img) => img.uploadedUrl);

    setPageState(PAGE_STATE.PROCESSING);
    setProcessingError(null);
    setAutoFillMsgIndex(0);

    // ── Step 1: AI Analysis ───────────────────────────────────
    let aiData;
    try {
      const aiForm = new FormData();
      successImages.forEach((img) => {
        if (img.file) aiForm.append("images", img.file);
      });
      aiForm.append("companyid", company_id || "");

      aiData = await axios
        .post(`${API_BASE}/multi-model/analyze`, aiForm, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
    } catch (err) {
      console.error("[SorterPage] AI analysis failed:", err);
      setPageState(PAGE_STATE.ERROR);
      setProcessingError("AI analysis failed. Please try again.");
      return;
    }

    // ── Step 2: Save to DB ────────────────────────────────────
    setPageState(PAGE_STATE.SAVING);

    try {
      const saveRes = await axios.post(`${API_BASE}/smartscan/save`, {
        company_id,
        user_id,
        analysisData: aiData,
        uploadedImageUrls: uploadedUrls,
      });

      if (saveRes.data.success) {
        console.log(
          `[SorterPage] Saved scan ${saveRes.data.smartscan_id}, ` +
            `${saveRes.data.images_saved} images saved.`,
        );
      }
    } catch (saveErr) {
      console.error(
        "[SorterPage] Save failed:",
        saveErr.response?.data || saveErr.message,
      );
      setPageState(PAGE_STATE.ERROR);
      setProcessingError(
        "Analysis succeeded but saving failed. Please try again — no data was lost.",
      );
      return;
    }

    // ── Step 3: Build result and update UI ────────────────────
    const analysis = aiData.analysis || {};
    const metadata = aiData.product_metadata || {};
    const price = aiData.price || {};
    const authenticity = aiData.authenticity || {};
    const condition = aiData.condition || {};
    const category = analysis.category || {};

    const authScore = authenticity.score ?? 0;
    const condScore = normalizeConditionScore(condition.score ?? 0);

    const rawItem = {
      title: metadata.title || analysis.seo_title || "Unknown Product",
      sku: metadata.sku || "",
      brand: analysis.brand || "",
      color: analysis.color || "",
      material: analysis.material || "",
      l1_gender: category.l1_gender || null,
      l2_type: category.l2_type || null,
      l3_category: category.l3_category || null,
      l4_subcategory: category.l4_subcategory || null,
      authenticity_score: authScore,
      authenticity_summary: authenticity.summary || "",
      condition_score: condScore,
      condition_summary: condition.summary || "",
      currency: price.currency || "USD",
      price_low: price.low ?? null,
      price_high: price.high ?? null,
      price_recommended: price.recommended ?? null,
      description: metadata.description || analysis.seo_description || "",
      seo_title: analysis.seo_title || "",
      seo_tags: analysis.seo_tags || metadata.seo_tags || "",
      sell_channel: getThriftopsSuggestion(authScore, condScore),
      sell_reason: aiData.thriftops_suggestion?.reason || "",
      images: uploadedUrls,
    };

    setResults((prev) => [
      mapToResultRow(
        rawItem,
        displayPricePercentage,
        user,
        uploadedUrls[0] || null,
      ),
      ...prev,
    ]);
    setImages([]); // safe to clear now
    setPageState(PAGE_STATE.IDLE);

    setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  // ── Sell Online ───────────────────────────────────────────
  const handleSellOnline = (index) => {
    const result = results[index];
    if (!result?.raw) return;
    const item = result.raw;

    const categoryString = [
      item.l1_gender,
      item.l2_type,
      item.l3_category,
      item.l4_subcategory,
    ]
      .filter(Boolean)
      .join(" > ");

    const priceRange =
      item.price_low != null && item.price_high != null
        ? `${item.price_low} - ${item.price_high}`
        : "";

    const conditionScore = normalizeConditionScore(item.condition_score);
    const conditionLabel =
      conditionScore >= 10
        ? "NEW"
        : conditionScore >= 9
          ? "LIKE-NEW"
          : conditionScore >= 8
            ? "EXCELLENT"
            : conditionScore >= 7
              ? "GOOD"
              : conditionScore >= 6
                ? "FAIR"
                : "POOR";

    const tags = item.seo_tags
      ? item.seo_tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
      : [];

    const uploadedImages = Array.isArray(item.images) ? item.images : [];

    setAddProductState({
      currentStep: 2,
      formData: {
        uploadedImages,
        categoryAspects: [],
        allCategoryAspects: [],
        categoryId: "",
        blurStatuses: uploadedImages.map(() => false),
        imageProcessing: false,
        backgroundRemoval: false,
        blurDetection: false,
        upscaleImage: false,
        title: item.title || "",
        description: item.description || "",
        sku: item.sku || "",
        price: item.price_recommended || "",
        pricerange: priceRange,
        quantity: "1",
        brand: item.brand || "",
        color: item.color || "",
        category: categoryString,
        condition: conditionLabel,
        condition_score: item.condition_score ?? "",
        condition_summary: item.condition_summary || "",
        authenticity_score: item.authenticity_score || "",
        authenticity_summary: item.authenticity_summary || "",
        material: item.material || "",
        tags,
        metafields: {},
        variants: [{ id: 1, size: "", color: "", sku: "" }],
        selectedMarketplaces: [],
        poshmarkCategory: "Women",
        poshmarkSubCategory: "",
        poshmarkSizeType: "standard",
        poshmarkAvailability: "available",
        vintedCategory: "",
        vintedCategoryId: "",
        vintedCategoryPath: [],
        vintedPackageSize: "medium",
        vintedSize: "",
      },
    });
    navigate("/products/add");
  };

  // ── Modal ─────────────────────────────────────────────────
  const openReasonModal = (type, score, reasons, description) => {
    setModalData({ type, score, reasons: reasons || [], description });
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalData(null);
  };

  const handleDismissError = () => {
    setProcessingError(null);
    setPageState(PAGE_STATE.IDLE);
  };

  // ─────────────────────────────────────────────
  // RENDER HELPERS
  // ─────────────────────────────────────────────
  const renderImageStatus = (img) => {
    switch (img.uploadStatus) {
      case UPLOAD_STATUS.UPLOADING:
        return (
          <div className="upload-status uploading">
            <div className="spinner" />
            <span>Uploading...</span>
          </div>
        );
      case UPLOAD_STATUS.SUCCESS:
        return (
          <div className="upload-status success">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Uploaded</span>
          </div>
        );
      case UPLOAD_STATUS.FAILED:
        return (
          <div className="upload-status failed">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <span>{img.error}</span>
          </div>
        );
      default:
        return null;
    }
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <>
      {(pageState === PAGE_STATE.PROCESSING ||
        pageState === PAGE_STATE.SAVING) && (
        <AutoFillLoader
          message={
            pageState === PAGE_STATE.SAVING
              ? "Saving your results..."
              : AUTO_FILL_MESSAGES[autoFillMsgIndex]
          }
        />
      )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleReplaceFileChange}
      />

      <div className="sorter-page">
        <div className="sorter-container">
          {/* ── Error banner ───────────────────────────────── */}
          {pageState === PAGE_STATE.ERROR && processingError && (
            <div className="error-banner">
              <span>{processingError}</span>
              <button onClick={handleDismissError}>Dismiss</button>
            </div>
          )}

          {/* ── Upload section ─────────────────────────────── */}
          <div className="image-preview-section sorter-upload-section">
            <div
              className="sorter-upload-area"
              onClick={handleUploadAreaClick}
              onDragOver={handleDragOver}
              onDrop={handleDragDrop}
            >
              {images.length > 0 ? (
                <div className="images-grid sorter-image-preview-grid">
                  {images.map((img) => (
                    <div
                      key={img.id}
                      className={`preview-card preview-card--${img.uploadStatus}`}
                    >
                      {/* Remove button */}
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(img.id);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>

                      {/* Image preview */}
                      <img
                        src={img.preview}
                        alt={img.file?.name || "Image"}
                        className="preview-image"
                      />

                      {/* Upload status overlay */}
                      {renderImageStatus(img)}

                      {/* Replace button — only for failed images */}
                      {img.uploadStatus === UPLOAD_STATUS.FAILED && (
                        <button
                          type="button"
                          className="replace-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReplaceClick(img.id);
                          }}
                        >
                          Replace Image
                        </button>
                      )}

                      <div className="preview-info">
                        <p className="file-name">{img.file?.name || "Image"}</p>
                        <p className="file-size">
                          {img.file?.size
                            ? `${(img.file.size / 1024).toFixed(1)} KB`
                            : ""}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Add more card */}
                  {!isBusy && (
                    <div className="sorter-add-more-card">
                      <div className="upload-icon">+</div>
                      <p>Add more images</p>
                      <span className="add-more-hint">Click or drop here</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="upload-placeholder">
                  <div className="upload-icon">📷</div>
                  <h3>Drop Images OR Click to Upload</h3>
                  <p>JPG, PNG, WebP • Max 5MB</p>
                </div>
              )}
            </div>

            {/* ── Action buttons ─────────────────────────── */}
            {images.length > 0 && (
              <div className="preview-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClearAll();
                  }}
                  disabled={isBusy}
                  style={{ touchAction: "manipulation" }}
                >
                  Clear All ({images.length})
                </button>

                <div className="process-btn-wrapper">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleProcessImages();
                    }}
                    disabled={!canProcess}
                    style={{ touchAction: "manipulation" }}
                    title={
                      anyFailed
                        ? "Remove or replace failed images first"
                        : anyUploading
                          ? "Please wait for uploads to complete"
                          : anyPending
                            ? "Please wait..."
                            : ""
                    }
                  >
                    {anyUploading || anyPending
                      ? "Uploading..."
                      : `Process ${images.length} Image${images.length !== 1 ? "s" : ""}`}
                  </button>

                  {/* Hint below button explaining why it's disabled */}
                  {anyFailed && (
                    <p className="process-btn-hint error">
                      Some images failed to upload. Remove or replace them to
                      continue.
                    </p>
                  )}
                  {anyUploading && (
                    <p className="process-btn-hint">
                      Waiting for uploads to finish...
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Loading state ──────────────────────────────── */}
          {pageState === PAGE_STATE.LOADING && (
            <div className="results-loading">Loading your saved scans...</div>
          )}

          {/* ── Results table ──────────────────────────────── */}
          {results.length > 0 && pageState !== PAGE_STATE.LOADING && (
            <div className="results-section" ref={resultsRef}>
              <h2 className="results-title">Processing Results</h2>
              <div className="result-table-wrapper">
                <table className="result-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Product Description</th>
                      <th>Potential Price</th>
                      <th>Authentication Score</th>
                      <th>Condition Score</th>
                      <th>Thriftops Suggestion</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result, index) => (
                      <tr key={index} className="result-row">
                        <td className="image-cell" data-label="Image">
                          {result.image && (
                            <img
                              src={result.image}
                              alt={result.description}
                              className="result-thumbnail"
                            />
                          )}
                        </td>
                        <td
                          className="description-cell"
                          data-label="Product Description"
                        >
                          <div className="description-text">
                            {result.description}
                          </div>
                        </td>
                        <td className="price-cell" data-label="Potential Price">
                          <span className="price-value">
                            {result.unit} {result.price}
                          </span>
                        </td>
                        <td
                          className="score-cell"
                          data-label="Authentication Score"
                        >
                          <div className="score-with-icon">
                            <div className="score-badge score-auth">
                              {result.authScore}%
                            </div>
                            <button
                              className="eye-icon-btn"
                              onClick={() =>
                                openReasonModal(
                                  "Authentication",
                                  result.authScore,
                                  result.authReasons,
                                  result.description,
                                )
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td className="score-cell" data-label="Condition Score">
                          <div className="score-with-icon">
                            <div className="score-badge score-condition">
                              {result.conditionScore}/10
                            </div>
                            <button
                              className="eye-icon-btn"
                              onClick={() =>
                                openReasonModal(
                                  "Condition",
                                  result.conditionScore,
                                  result.conditionReasons,
                                  result.description,
                                )
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td
                          className="suggestion-cell"
                          data-label="Thriftops Suggestion"
                        >
                          <span className="suggestion-text">
                            {result.suggestion}
                          </span>
                        </td>
                        <td className="actions-cell" data-label="Actions">
                          <div className="action-buttons">
                            <button
                              className="btn-primary btn-sm"
                              onClick={() => handleSellOnline(index)}
                              disabled={
                                !isConditionAcceptable(
                                  result.conditionScore,
                                  minimumAcceptableCondition,
                                )
                              }
                              title={
                                !isConditionAcceptable(
                                  result.conditionScore,
                                  minimumAcceptableCondition,
                                )
                                  ? "Product condition is not acceptable"
                                  : "Sell online"
                              }
                            >
                              Sell Online
                            </button>
                            <button
                              className="btn-secondary btn-sm"
                              disabled
                              title="Coming soon"
                            >
                              Push to Retail
                            </button>
                            <button
                              className="btn-secondary btn-sm"
                              disabled
                              title="Coming soon"
                            >
                              Wholesale
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Reason modal ───────────────────────────────── */}
          {modalOpen && modalData && (
            <div className="reason-modal-overlay" onClick={closeModal}>
              <div
                className="reason-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <div className="modal-title-row">
                    <div className="modal-title-section">
                      <h2 className="modal-product-title">
                        {modalData.description}
                      </h2>
                      <h3 className="modal-analysis-type">
                        {modalData.type} Analysis
                      </h3>
                    </div>
                    <div className="modal-score">
                      <span className="score-label">Score</span>
                      <span
                        className={`score-value ${modalData.type === "Authentication" ? "score-auth" : "score-condition"}`}
                      >
                        {modalData.score}/10
                      </span>
                    </div>
                  </div>
                </div>
                <div className="modal-body">
                  <h4 className="reasons-heading">Analysis Details:</h4>
                  <ul className="reasons-list">
                    {modalData.reasons?.length > 0 ? (
                      modalData.reasons.map((reason, idx) => (
                        <li
                          key={idx}
                          className={`reason-item reason-${reason.status}`}
                        >
                          <span className="reason-icon">
                            {reason.status === "pass" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                              </svg>
                            ) : reason.status === "warning" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" />
                                <line x1="12" y1="17" x2="12.01" y2="17" />
                              </svg>
                            ) : null}
                          </span>
                          <span className="reason-text">{reason.text}</span>
                        </li>
                      ))
                    ) : (
                      <li className="reason-item reason-pass">
                        <span className="reason-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                          </svg>
                        </span>
                        <span className="reason-text">
                          No detailed analysis available. Process the images
                          again to generate details.
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button className="modal-btn-close" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SorterPage;
